import * as THREE from 'three';
import { CELLARS } from '@somnhold/content';
import { hexDist, hexToWorld, type BattleState, type DungeonLayout, type ExpDevice, type Expedition, type ExpItem, type Hex } from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { facingToRotY, WORLD_PER_HEX } from './coords.ts';
import type { PuzzleFx } from './puzzles.ts';

/*
 * The garrison cellars (docs/EXPEDICIONES.md), what the expedition puts on the crypt's floor:
 *
 * - Devices: the cisterns' valve wheels (a flat iron wheel on a pipe stem; it spins while worked and sets
 *   still with a cold glint once open), the tower's lever on a pillar with an arrow slit (it tilts as it is
 *   held and drops when thrown), each wing's lock (an iron plate with a heavy bolt that slides out), the well's
 *   winch at the floodgate hall (stone well mouth, a frame with a pulley, a crank that turns while the bell
 *   goes up), the powder barrels and the rubble on the tower's stair (gone once blown).
 * - A ring on the floor under every device that standing by works: a dark track, filling in pale as it is held.
 * - Items: a powder keg (a sputtering spark while its fuse burns), the cells' key (bobbing, a pale glow) and
 *   the gate bell (dark iron gone green). A carried load rides over its carrier's head; the bell hangs low
 *   between its two bearers, and on the hook it rises up the well as the winch turns.
 * - Water: the first cistern and the flooded passage to the powder store lie under dark water until both
 *   wheels are open; the great cistern too, until the floodgate opens: then the black ink rises, the run's clock.
 *
 * Static parts of every device are merged into two meshes (stone and wood, iron); what moves is its own mesh.
 */

const K = WORLD_PER_HEX / 1000;
const S = WORLD_PER_HEX;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const UP = new THREE.Vector3(0, 1, 0);
const PALE = new THREE.Color('#dfe4ff');
const COLD = new THREE.Color('#9fb8d0');
const RED = new THREE.Color('#ff4f3a');
const SPARK = new THREE.Color('#ff7a2a');

/* ---------- painted parts: vertex colours, merged by material ---------- */

interface Part {
  g: THREE.BufferGeometry;
  c: string;
  iron?: boolean;
}

const paint = (g: THREE.BufferGeometry, color: string): THREE.BufferGeometry => {
  const n = g.toNonIndexed();
  n.deleteAttribute('uv');
  const c = new THREE.Color(color);
  const cnt = n.getAttribute('position').count;
  const a = new Float32Array(cnt * 3);
  for (let i = 0; i < cnt; i++) a.set([c.r, c.g, c.b], i * 3);
  n.setAttribute('color', new THREE.Float32BufferAttribute(a, 3));
  return n;
};

/** Merges parts (each already placed) into one geometry per material: [stone and wood, iron]. */
const mergeParts = (parts: Part[], m?: THREE.Matrix4): [THREE.BufferGeometry | null, THREE.BufferGeometry | null] => {
  const pick = (iron: boolean): THREE.BufferGeometry | null => {
    const gs = parts.filter((p) => !!p.iron === iron).map((p) => {
      const g = paint(p.g, p.c);
      if (m) g.applyMatrix4(m);
      return g;
    });
    return gs.length ? (mergeGeometries(gs) as THREE.BufferGeometry) : null;
  };
  return [pick(false), pick(true)];
};

const box = (w: number, h: number, d: number, x: number, y: number, z: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(x, y, z);
const cyl = (r0: number, r1: number, h: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry => new THREE.CylinderGeometry(r0, r1, h, seg).translate(x, y, z);

const IRON = '#34343a';
const IRON_D = '#232328';
const STONE = '#4a4c53';
const STONE_D = '#3a3c42';
const WOOD = '#3a2a20';
const WOOD_D = '#2b1f18';
const ROPE = '#5d564e';
const VERDIGRIS = '#3f4c48';
const POWDER = '#6e1f24';

/** A powder keg upright, `h` tall: a bulging stave body, iron hoops, a dark red band. */
const kegParts = (h: number, x = 0, z = 0, y = 0): Part[] => {
  const r = h * 0.36;
  const prof: THREE.Vector2[] = [];
  for (let i = 0; i <= 6; i++) {
    const t = i / 6;
    prof.push(new THREE.Vector2(r * (0.82 + 0.18 * Math.sin(t * Math.PI)), t * h));
  }
  const body = new THREE.LatheGeometry(prof, 9).translate(x, y, z);
  const lid = cyl(r * 0.8, r * 0.8, 0.012, 9, x, y + h - 0.004, z);
  const parts: Part[] = [
    { g: body, c: WOOD },
    { g: lid, c: WOOD_D },
    { g: cyl(r * 0.99, r * 0.99, h * 0.12, 9, x, y + h * 0.5, z), c: POWDER },
  ];
  for (const t of [0.12, 0.88]) parts.push({ g: cyl(r * (0.84 + 0.18 * Math.sin(t * Math.PI)) + 0.006, r * (0.84 + 0.18 * Math.sin(t * Math.PI)) + 0.006, h * 0.06, 9, x, y + t * h, z), c: IRON, iron: true });
  return parts;
};

/** The gate bell: a wide iron bell gone green, its crown loop and the clapper under it. About 0.6 tall. */
const bellGeo = (): THREE.BufferGeometry => {
  const prof = [
    [0.0, 0.6], [0.09, 0.6], [0.13, 0.57], [0.15, 0.48], [0.17, 0.32], [0.22, 0.16], [0.3, 0.05], [0.32, 0.0], [0.28, 0.0], [0.2, 0.08], [0.0, 0.08],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const parts: Part[] = [
    { g: new THREE.LatheGeometry(prof.reverse(), 14), c: VERDIGRIS, iron: true },
    { g: new THREE.TorusGeometry(0.06, 0.018, 5, 10).translate(0, 0.66, 0), c: IRON, iron: true },
    { g: new THREE.SphereGeometry(0.05, 6, 5).translate(0, 0.06, 0), c: IRON_D, iron: true },
    { g: cyl(0.25, 0.25, 0.02, 14, 0, 0.11, 0), c: '#4c5a55', iron: true },
  ];
  return mergeParts(parts)[1] as THREE.BufferGeometry;
};

/** The cells' key: a ring bow, a long shank, a toothed bit. Lies along +x, about 0.4 long. */
const keyGeo = (): THREE.BufferGeometry => {
  const parts: Part[] = [
    { g: new THREE.TorusGeometry(0.07, 0.018, 5, 12).rotateX(Math.PI / 2).translate(-0.16, 0, 0), c: '#5a5c64', iron: true },
    { g: cyl(0.015, 0.015, 0.3, 6, 0, 0, 0).rotateZ(Math.PI / 2).translate(0.02, 0, 0), c: '#5a5c64', iron: true },
    { g: box(0.05, 0.02, 0.07, 0.15, 0, 0.04), c: '#5a5c64', iron: true },
    { g: box(0.025, 0.02, 0.05, 0.1, 0, 0.035), c: '#5a5c64', iron: true },
  ];
  return mergeParts(parts)[1] as THREE.BufferGeometry;
};

/* ---------- the progress rings ---------- */

const RING_VERT = /* glsl */ `
attribute vec2 aLocal;
attribute vec4 aState;
varying vec2 vL;
varying vec4 vS;
void main() {
  vL = aLocal;
  vS = aState;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/** x progress (0 → 1), y shown (0 hidden, 0.5 idle and workable, 1 being worked), z the flash once done. */
const RING_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vL;
varying vec4 vS;
void main() {
  float d = length(vL);
  float ang = atan(vL.x, vL.y) / 6.2831 + 0.5;
  float band = smoothstep(0.8, 0.83, d) * (1.0 - smoothstep(0.93, 0.96, d));
  float fill = step(ang, vS.x) * step(0.001, vS.x);
  float notch = step(0.93, fract(ang * 8.0));
  float pulse = 0.75 + 0.25 * sin(uTime * 5.0);
  vec3 col = mix(vec3(0.15, 0.16, 0.19), vec3(0.92, 0.94, 1.0) * 1.25 * pulse, fill) * (1.0 - notch * 0.55);
  float inner = (1.0 - smoothstep(0.0, 0.8, d)) * (0.1 * vS.x + 0.5 * vS.z);
  col += vec3(0.85, 0.9, 1.0) * inner + vec3(0.9, 0.93, 1.0) * band * vS.z;
  float a = (band * (0.45 + 0.55 * max(fill, vS.z)) + inner) * max(vS.y, vS.z);
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/* ---------- the layer ---------- */

interface DeviceView {
  d: number;
  kind: ExpDevice['kind'];
  at: THREE.Vector3;
  yaw: number;
  /** What moves: a wheel, a lever's handle, a bolt, the winch's crank and drum; the rubble pile. */
  part: THREE.Object3D | null;
  /** Where it should be (lever thrown, bolt drawn: 1) and where it is drawn, eased toward it. */
  want: number;
  ease: number;
  lastHold: number;
  turning: boolean;
  flash: number;
}

export class CellarsLayer {
  readonly group = new THREE.Group();
  private devs: DeviceView[] = [];
  private items = new Map<number, THREE.Mesh>();
  private ring: { mesh: THREE.Mesh; state: THREE.BufferAttribute } | null = null;
  private u = { uTime: { value: 0 } };
  private mats: THREE.Material[] = [];
  private prop: THREE.MeshStandardMaterial;
  private iron: THREE.MeshStandardMaterial;
  private ironSet: THREE.MeshStandardMaterial;
  /** A keg, one geometry (the hoops painted on the wood: a carried keg is small). */
  private kegG: THREE.BufferGeometry;
  private bellG = bellGeo();
  private keyG = keyGeo();
  /** The first cistern and the passage to the powder store: water until both wheels open (eased level 1 → 0). */
  private waterA: THREE.Mesh | null = null;
  private drainA = 1;
  private drainedA = false;
  /** The great cistern: water, then ink. */
  private waterC: THREE.Mesh | null = null;
  private ink: THREE.Mesh | null = null;
  private inkK = 0;
  /** The bell, once raised: how far past the top it has gone (fades out). */
  private bellGone = 0;
  private raised = false;
  private winch: DeviceView | null = null;
  private rope: THREE.Mesh;
  private winchHold = 0;
  private lastNow = 0;
  private q = new THREE.Quaternion();
  private sc = new THREE.Vector3(1, 1, 1);

  constructor(
    private layout: DungeonLayout,
    private ground: (x: number, z: number) => number,
    x: Expedition,
    private fx: PuzzleFx,
  ) {
    this.group.name = 'cellars';
    const own = <T extends THREE.Material>(m: T): T => {
      this.mats.push(m);
      return m;
    };
    this.prop = own(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, metalness: 0, flatShading: true }));
    this.iron = own(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.42, metalness: 0.75, flatShading: true }));
    this.ironSet = own(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.3, metalness: 0.8, flatShading: true, emissive: '#24384a', emissiveIntensity: 0.9 }));
    this.kegG = mergeGeometries(mergeParts(kegParts(0.34, 0, 0, -0.17)).filter((g): g is THREE.BufferGeometry => !!g)) as THREE.BufferGeometry;
    const rope = new THREE.CylinderGeometry(0.012, 0.012, 1, 4).translate(0, 0.5, 0);
    this.rope = new THREE.Mesh(paint(rope, ROPE), this.prop);
    this.rope.visible = false;
    this.group.add(this.rope);
    this.buildDevices(x);
    this.buildWater(x);
  }

  private at(h: Hex, lift = 0): THREE.Vector3 {
    const c = hexToWorld(h.q, h.r);
    const px = c.x * K, pz = c.y * K;
    return new THREE.Vector3(px, this.ground(px, pz) + lift, pz);
  }

  private roomCenter(i: number): THREE.Vector3 {
    const rm = this.layout.rooms[i];
    return rm ? this.at(rm) : new THREE.Vector3();
  }

  private buildDevices(x: Expedition): void {
    const statics: Part[][] = [[], []];
    const hub = x.rooms.hub >= 0 ? this.roomCenter(x.rooms.hub) : new THREE.Vector3();
    const ringAt: { p: THREE.Vector3; half: number }[] = [];
    x.devices.forEach((d, i) => {
      const at = this.at(d);
      const center = this.roomCenter(d.room);
      // Facing: toward the room's centre; a device at the centre faces the floodgate hall (the winch, the way in).
      let face = center.clone().sub(at).setY(0);
      if (face.lengthSq() < 0.01) face = hub.clone().sub(at).setY(0);
      if (face.lengthSq() < 0.01) face.set(0, 0, -1);
      let yaw = Math.atan2(face.x, face.z);
      // The cells' lock stands beside the floodgate's frame, facing the hall.
      if (d.kind === 'lock' && d.wing === 'cells') {
        const into = this.layout.rooms[x.rooms.cistern];
        const toward = into ? this.at(into).sub(at).setY(0).normalize() : new THREE.Vector3(0, 0, 1);
        yaw = Math.atan2(-toward.x, -toward.z);
        at.addScaledVector(new THREE.Vector3(toward.z, 0, -toward.x), 0.45).addScaledVector(toward, -0.35);
      }
      const v: DeviceView = { d: i, kind: d.kind, at, yaw, part: null, want: d.done ? 1 : 0, ease: d.done ? 1 : 0, lastHold: d.hold, turning: false, flash: 0 };
      const local = this.deviceParts(v);
      const m = new THREE.Matrix4().compose(at, this.q.setFromAxisAngle(UP, yaw), this.sc.set(1, 1, 1));
      const [a, b] = mergeParts(local, m);
      if (a) statics[0]!.push({ g: a, c: '#ffffff' });
      if (b) statics[1]!.push({ g: b, c: '#ffffff', iron: true });
      if (v.part) {
        v.part.userData.base = v.part.position.clone();
        const g = new THREE.Group();
        g.position.copy(at);
        g.rotation.y = yaw;
        g.add(v.part);
        this.group.add(g);
      }
      if (d.kind === 'winch') this.winch = v;
      if (d.need > 0) ringAt.push({ p: at.clone().setY(at.y + 0.09), half: (((d.kind === 'winch' ? CELLARS.winch.range : CELLARS.device.range) / 1000) * S) / 0.88 });
      else ringAt.push({ p: at.clone().setY(-50), half: 0.01 });
      this.devs.push(v);
    });
    // The static parts, already in place: their colours multiply white.
    for (const [k, list] of statics.entries()) {
      if (!list.length) continue;
      const g = mergeGeometries(list.map((p) => p.g)) as THREE.BufferGeometry;
      const mesh = new THREE.Mesh(g, k ? this.iron : this.prop);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    }
    if (ringAt.length) this.ring = this.rings(ringAt);
  }

  /** A device's static parts in its own frame (front +z); sets `v.part` to what moves. */
  private deviceParts(v: DeviceView): Part[] {
    const P: Part[] = [];
    switch (v.kind) {
      case 'wheel': {
        // A pipe up out of the floor, a flange, and a flat valve wheel on top; the pipe runs back to the wall.
        P.push({ g: cyl(0.1, 0.12, 0.08, 10, 0, 0.04, 0), c: IRON_D, iron: true });
        P.push({ g: cyl(0.055, 0.055, 0.5, 8, 0, 0.3, 0), c: IRON, iron: true });
        P.push({ g: cyl(0.08, 0.08, 0.05, 8, 0, 0.32, 0), c: IRON_D, iron: true });
        P.push({ g: cyl(0.07, 0.07, 1.3, 8, 0, 0, 0).rotateX(Math.PI / 2).translate(0, 0.07, -0.65), c: IRON_D, iron: true });
        P.push({ g: box(0.34, 0.08, 0.2, 0, 0.04, -0.2), c: STONE_D });
        const wheel = [
          new THREE.TorusGeometry(0.3, 0.032, 6, 20).rotateX(Math.PI / 2),
          ...[0, 1, 2].map((k) => new THREE.BoxGeometry(0.58, 0.025, 0.035).rotateY((k * Math.PI) / 3)),
          new THREE.CylinderGeometry(0.06, 0.06, 0.07, 8),
          ...[0, 1, 2, 3, 4, 5].map((k) => new THREE.SphereGeometry(0.035, 5, 4).translate(Math.cos((k * Math.PI) / 3 + 0.5) * 0.3, 0, Math.sin((k * Math.PI) / 3 + 0.5) * 0.3)),
        ].map((g) => paint(g, '#4a4a52'));
        const mesh = new THREE.Mesh(mergeGeometries(wheel) as THREE.BufferGeometry, this.iron);
        mesh.position.y = 0.58;
        mesh.castShadow = true;
        v.part = mesh;
        break;
      }
      case 'lever': {
        // A stone pillar pierced by an arrow slit (dark), an iron plate and the lever on its front.
        P.push({ g: box(0.5, 1.05, 0.34, 0, 0.525, 0), c: STONE });
        P.push({ g: box(0.58, 0.1, 0.4, 0, 0.05, 0), c: STONE_D });
        P.push({ g: box(0.56, 0.08, 0.4, 0, 1.08, 0), c: STONE_D });
        P.push({ g: box(0.06, 0.42, 0.36, 0, 0.72, 0), c: '#08090b' });
        P.push({ g: box(0.18, 0.28, 0.03, 0.14, 0.42, 0.18), c: IRON_D, iron: true });
        const handle = [cyl(0.022, 0.022, 0.36, 6, 0, 0.18, 0), new THREE.SphereGeometry(0.05, 7, 5).translate(0, 0.37, 0), cyl(0.04, 0.04, 0.08, 8, 0, 0, 0).rotateZ(Math.PI / 2)].map((g) => paint(g, '#4a4850'));
        const mesh = new THREE.Mesh(mergeGeometries(handle) as THREE.BufferGeometry, this.iron);
        mesh.position.set(0.14, 0.42, 0.22);
        mesh.castShadow = true;
        v.part = mesh;
        break;
      }
      case 'lock': {
        // A stone block, an upright iron plate with two staples, and a heavy bolt across them (slides out).
        P.push({ g: box(0.66, 0.24, 0.36, 0, 0.12, 0), c: STONE_D });
        P.push({ g: box(0.56, 0.56, 0.07, 0, 0.52, 0), c: IRON_D, iron: true });
        for (const sx of [-0.19, 0.19]) P.push({ g: box(0.07, 0.12, 0.08, sx, 0.58, 0.06), c: IRON, iron: true });
        for (const sx of [-0.22, 0.22]) for (const sy of [0.3, 0.74]) P.push({ g: new THREE.SphereGeometry(0.022, 5, 4).translate(sx, sy, 0.04), c: '#4a4a52', iron: true });
        P.push({ g: box(0.07, 0.1, 0.02, 0, 0.42, 0.045), c: '#0b0b0d' });
        const bolt = [cyl(0.04, 0.04, 0.5, 8, 0, 0, 0).rotateZ(Math.PI / 2), new THREE.SphereGeometry(0.055, 7, 5).translate(0.27, 0, 0), box(0.03, 0.12, 0.03, 0.14, 0.06, 0)].map((g) => paint(g, '#55555d'));
        const mesh = new THREE.Mesh(mergeGeometries(bolt) as THREE.BufferGeometry, this.iron);
        mesh.position.set(0, 0.58, 0.11);
        mesh.castShadow = true;
        v.part = mesh;
        break;
      }
      case 'winch': {
        // The well: a stone mouth around a black shaft; over it a frame with a pulley; the crank drum beside it.
        const prof = [[0.42, 0], [0.56, 0], [0.58, 0.3], [0.5, 0.34], [0.42, 0.3]].map(([a, b]) => new THREE.Vector2(a, b));
        P.push({ g: new THREE.LatheGeometry(prof, 12), c: STONE });
        P.push({ g: new THREE.CircleGeometry(0.43, 12).rotateX(-Math.PI / 2).translate(0, 0.02, 0), c: '#040405' });
        for (const sx of [-0.66, 0.66]) {
          P.push({ g: box(0.11, 1.95, 0.11, sx, 0.975, 0), c: WOOD });
          P.push({ g: box(0.2, 0.08, 0.3, sx, 0.04, 0), c: WOOD_D });
        }
        P.push({ g: box(1.46, 0.1, 0.12, 0, 1.92, 0), c: WOOD_D });
        P.push({ g: cyl(0.09, 0.09, 0.05, 10, 0, 0, 0).rotateZ(Math.PI / 2).translate(0, 1.8, 0), c: IRON, iron: true });
        P.push({ g: box(0.03, 0.14, 0.03, 0, 1.86, 0), c: IRON_D, iron: true });
        // The drum's trestles at waist height, in front of the well.
        for (const sx of [-0.34, 0.34]) P.push({ g: box(0.07, 0.62, 0.07, sx, 0.31, 0.72), c: WOOD });
        const drum = [
          cyl(0.09, 0.09, 0.62, 10, 0, 0, 0).rotateZ(Math.PI / 2),
          ...[-0.12, 0, 0.12].map((dx) => paint(cyl(0.1, 0.1, 0.05, 10, dx, 0, 0).rotateZ(Math.PI / 2), ROPE)),
          box(0.04, 0.24, 0.04, 0.4, 0.1, 0),
          cyl(0.02, 0.02, 0.16, 6, 0, 0, 0).rotateZ(Math.PI / 2).translate(0.47, 0.2, 0),
        ].map((g) => (g.getAttribute('color') ? g : paint(g, WOOD_D)));
        const mesh = new THREE.Mesh(mergeGeometries(drum) as THREE.BufferGeometry, this.prop);
        mesh.position.set(0, 0.62, 0.72);
        mesh.castShadow = true;
        v.part = mesh;
        break;
      }
      case 'barrels': {
        // The powder store's stack: three kegs, one more lying across them, a broken stave.
        for (const [bx, bz] of [[-0.24, 0.05], [0.22, 0.08], [0, -0.26]] as const) P.push(...kegParts(0.44, bx, bz));
        for (const p of kegParts(0.42)) {
          p.g.translate(0, -0.21, 0).rotateZ(Math.PI / 2).rotateY(0.4).translate(0, 0.62, -0.02);
          P.push(p);
        }
        P.push({ g: box(0.05, 0.02, 0.3, 0.42, 0.01, 0.32).rotateY(0.2), c: WOOD_D });
        break;
      }
      case 'rubble': {
        // Fallen stone heaped in the doorway: a pile of blocks (gone once blown).
        const parts: THREE.BufferGeometry[] = [];
        let seed = (this.devs.length + 3) * 7919;
        const rnd = (): number => ((seed = (seed * 16807) % 2147483647) / 2147483647);
        for (let k = 0; k < 14; k++) {
          const a = rnd() * Math.PI * 2;
          const r = rnd() * 0.5;
          const s = 0.14 + rnd() * 0.16;
          const y = s * 0.6 + (0.5 - r) * 0.5;
          parts.push(paint(new THREE.DodecahedronGeometry(s, 0).scale(1, 0.75, 1).rotateY(rnd() * 3).translate(Math.cos(a) * r, y, Math.sin(a) * r), k % 3 ? STONE : STONE_D));
        }
        parts.push(paint(box(0.7, 0.18, 0.16, 0, 0.45, 0.1).rotateZ(0.35), '#55575e'));
        const mesh = new THREE.Mesh(mergeGeometries(parts) as THREE.BufferGeometry, this.prop);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        v.part = mesh;
        break;
      }
    }
    return P;
  }

  /** Flat quads (local -1..1) at each centre, merged, with a per-vertex state. */
  private rings(at: { p: THREE.Vector3; half: number }[]): { mesh: THREE.Mesh; state: THREE.BufferAttribute } {
    const pos: number[] = [];
    const local: number[] = [];
    const index: number[] = [];
    at.forEach(({ p, half }, n) => {
      for (const [lx, lz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        pos.push(p.x + lx * half, p.y, p.z + lz * half);
        local.push(lx, -lz);
      }
      const b = n * 4;
      index.push(b, b + 2, b + 1, b, b + 3, b + 2);
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('aLocal', new THREE.Float32BufferAttribute(local, 2));
    const state = new THREE.Float32BufferAttribute(new Float32Array(at.length * 16), 4).setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aState', state);
    geo.setIndex(index);
    const mat = new THREE.ShaderMaterial({ uniforms: this.u, vertexShader: RING_VERT, fragmentShader: RING_FRAG, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -8 });
    this.mats.push(mat);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    this.group.add(mesh);
    return { mesh, state };
  }

  /** A room's floor as one hexagon (corners toward the six neighbours, like the rooms are laid). */
  private roomDisc(i: number, lift: number): THREE.BufferGeometry | null {
    const rm = this.layout.rooms[i];
    if (!rm) return null;
    const c = this.at(rm);
    return new THREE.CircleGeometry((rm.radius + 0.5) * S, 6).rotateX(-Math.PI / 2).translate(c.x, c.y + lift, c.z);
  }

  private buildWater(x: Expedition): void {
    const water = (color: string, opacity: number): THREE.MeshStandardMaterial => {
      const m = new THREE.MeshStandardMaterial({ color, roughness: 0.12, metalness: 0.35, transparent: true, opacity, depthWrite: false });
      this.mats.push(m);
      return m;
    };
    // The first cistern and the flooded passage on to the powder store.
    const parts: THREE.BufferGeometry[] = [];
    const disc = this.roomDisc(x.rooms.cisternA, 0);
    if (disc) parts.push(disc.toNonIndexed());
    const A = this.layout.rooms[x.rooms.powderA];
    const B = this.layout.rooms[x.rooms.powderB];
    if (A && B) {
      const span = hexDist(A, B);
      const inRoom = (h: Hex): boolean => this.layout.rooms.some((r) => hexDist(h, r) <= r.radius);
      for (const h of this.layout.floor) {
        if (inRoom(h) || hexDist(h, A) + hexDist(h, B) !== span) continue;
        const c = this.at(h);
        parts.push(new THREE.CircleGeometry(S * 0.6, 6).rotateX(-Math.PI / 2).rotateY(Math.PI / 6).translate(c.x, c.y, c.z).toNonIndexed());
      }
    }
    if (parts.length) {
      this.waterA = new THREE.Mesh(mergeGeometries(parts) as THREE.BufferGeometry, water('#0c1a21', 0.86));
      this.waterA.position.y = 0.07;
      this.waterA.renderOrder = 1;
      this.group.add(this.waterA);
    }
    const c = this.roomDisc(x.rooms.cistern, 0);
    if (c) {
      this.waterC = new THREE.Mesh(c, water('#0c1a21', 0.88));
      this.waterC.name = 'water';
      this.waterC.position.y = 0.07;
      this.waterC.renderOrder = 1;
      this.group.add(this.waterC);
      const inkM = new THREE.MeshStandardMaterial({ color: '#060509', roughness: 0.18, metalness: 0.2, emissive: '#160a24', emissiveIntensity: 0.5 });
      this.mats.push(inkM);
      this.ink = new THREE.Mesh(c.clone(), inkM);
      this.ink.name = 'ink';
      this.ink.visible = false;
      this.ink.receiveShadow = true;
      this.group.add(this.ink);
    }
  }

  /** Every sync: devices, rings, items and water from the state. `pos` is where each hero is drawn. */
  sync(s: BattleState, fresh: boolean, pos: (id: number) => THREE.Vector3 | undefined): void {
    const x = s.dungeon?.exp;
    if (!x) return;
    const tick = s.tick;
    const bell = x.items.find((it) => it.kind === 'bell');
    const st = this.ring?.state.array as Float32Array | undefined;
    this.devs.forEach((v) => {
      const d = x.devices[v.d];
      if (!d) return;
      if (fresh) {
        v.turning = !d.done && d.hold > v.lastHold;
        v.lastHold = d.hold;
      }
      const workable = d.need > 0 && !d.done && (d.kind !== 'winch' || !!bell?.hooked);
      const prog = d.need > 0 ? clamp01(d.hold / d.need) : 0;
      if (st) {
        const shown = workable ? (d.hold > 0 ? 1 : 0.5) : 0;
        for (let k = 0; k < 4; k++) st.set([prog, shown, v.flash, 0], (v.d * 4 + k) * 4);
      }
      if (fresh && v.flash > 0) v.flash = Math.max(0, v.flash - 0.08);
      const a = v.at;
      if (v.kind === 'wheel') {
        (v.part as THREE.Mesh).material = d.done ? this.ironSet : this.iron;
        if (d.done) this.fx.glow.add(a.x, a.y + 0.62, a.z, COLD, 0.9, 0.12 + 0.06 * Math.sin(tick * 0.35 + v.d));
        else if (v.turning) this.fx.glow.add(a.x, a.y + 0.62, a.z, PALE, 0.8, 0.1);
      } else if (v.kind === 'lever') {
        v.want = d.done ? 1 : prog * 0.6;
        if (d.done) this.fx.glow.add(a.x, a.y + 0.75, a.z, COLD, 0.7, 0.1);
      } else if (v.kind === 'lock') {
        v.want = d.done ? 1 : 0;
        if (!d.done) this.fx.glow.add(a.x, a.y + 0.45, a.z, RED, 0.55, 0.16 + 0.06 * Math.sin(tick * 0.5 + v.d));
        else this.fx.glow.add(a.x, a.y + 0.6, a.z, COLD, 0.7, 0.1);
      } else if (v.kind === 'rubble') {
        if (v.part) v.part.visible = !d.done;
      } else if (v.kind === 'winch') {
        this.winchHold = prog;
        if (d.done) this.raised = true;
      }
    });
    if (this.ring) this.ring.state.needsUpdate = true;
    this.syncItems(s, x, fresh, pos);
    this.syncWater(s, x, fresh);
  }

  private syncItems(s: BattleState, x: Expedition, fresh: boolean, pos: (id: number) => THREE.Vector3 | undefined): void {
    const seen = new Set<number>();
    const tick = s.tick;
    // The rope from the pulley: down to the bell once it hangs on the hook, otherwise an empty hook over the well.
    let ropeTo = (this.winch?.at.y ?? 0) + 0.95;
    for (const it of x.items) {
      seen.add(it.id);
      let m = this.items.get(it.id);
      if (!m) {
        m = this.itemMesh(it);
        this.items.set(it.id, m);
        this.group.add(m);
      }
      m.visible = true;
      const carriers = it.carriers.map((id) => ({ id, p: pos(id), h: s.heroes.find((h) => h.id === id) })).filter((c) => c.p && c.h);
      const floor = new THREE.Vector3(it.x * K, 0, it.y * K);
      floor.y = this.ground(floor.x, floor.z);
      m.rotation.set(0, 0, 0);
      if (it.kind === 'bell') {
        if (it.hooked && this.winch) {
          // On the hook: hung over the well, rising as the winch turns; once up, on into the dark.
          const w = this.winch.at;
          const y = w.y + 0.12 + this.winchHold * 0.95 + this.bellGone;
          m.position.set(w.x, y, w.z);
          m.rotation.y = tick * 0.02;
          m.visible = this.bellGone < 0.4;
          ropeTo = m.visible ? y + 0.68 : w.y + 1.4;
          if (this.raised) this.fx.glow.add(w.x, y + 0.3, w.z, PALE, 1.4, 0.2 * (1 - this.bellGone / 0.4));
        } else if (carriers.length >= 2) {
          // Slung low between its bearers.
          const a = carriers[0]!.p!;
          const b = carriers[1]!.p!;
          m.position.copy(a).lerp(b, 0.5).setY(Math.max(a.y, b.y) + 0.32 + Math.sin(tick * 0.6) * 0.02);
          m.rotation.z = Math.sin(tick * 0.45) * 0.08;
        } else m.position.copy(floor);
        continue;
      }
      const c = carriers[0];
      if (c) {
        // Carried: over the carrier's head, a little forward.
        const yaw = facingToRotY(c.h!.facing);
        m.position.copy(c.p!).add(new THREE.Vector3(Math.sin(yaw) * 0.1, 0.98, Math.cos(yaw) * 0.1));
        m.rotation.y = yaw;
        if (it.kind === 'barrel') m.rotation.z = Math.PI / 2;
      } else m.position.copy(floor).setY(floor.y + (it.kind === 'barrel' ? 0.17 : 0));
      if (it.kind === 'key') {
        // Bobbing over the floor with a pale glow, so it reads at a glance.
        if (!c) m.position.y += 0.32 + Math.sin(tick * 0.4) * 0.05;
        m.rotation.y += tick * 0.08;
        m.rotation.x = 0.5;
        this.fx.glow.add(m.position.x, m.position.y, m.position.z, PALE, 0.9, 0.22 + 0.08 * Math.sin(tick * 0.4));
      }
      if (it.kind === 'barrel' && it.fuse > 0) {
        // The fuse: a sputtering spark on top, faster as it burns down.
        const left = it.fuse - tick;
        const top = m.position.clone().setY(m.position.y + (c ? 0.15 : 0.19));
        const quick = left < CELLARS.barrel.fuse / 3;
        const blink = (tick % (quick ? 2 : 3)) === 0 ? 1 : 0.6;
        this.fx.glow.add(top.x, top.y, top.z, SPARK, 0.9 + 0.3 * blink, 0.5 * blink);
        this.fx.glow.add(top.x, top.y, top.z, RED, 1.6, 0.18);
        if (fresh) this.fx.vfx.burst(top, { n: this.fx.low ? 1 : quick ? 3 : 2, color: tick % 2 ? '#ffb060' : '#ff6a2a', speed: 0.03, up: 0.04, size: 0.05, life: 6, gravity: 0.006, spread: 0.03 });
        if (fresh) {
          // On the floor, how far it will hurt (CELLARS.barrel.blastRadius, round where the sim has it: its carrier's
          // feet or where it lies): a red line at the radius, blinking faster at the end, and a second one running
          // out to it as the fuse burns. Rings of the shared Vfx pool, no mesh or material per barrel.
          const R = (CELLARS.barrel.blastRadius / 1000) * S;
          const k = clamp01(1 - left / Math.max(1, CELLARS.barrel.fuse));
          const g = floor.setY(floor.y + 0.05);
          this.fx.vfx.ring(g, R * 0.97, R, '#ff4f3a', 2, 0.55 + (tick % (quick ? 2 : 4) < (quick ? 1 : 2) ? 0.3 : 0));
          const r = Math.max(0.1, R * k);
          this.fx.vfx.ring(g, r * 0.97, r, '#ff8a6e', 2, 0.4 + 0.4 * k);
        }
      }
    }
    if (this.winch) {
      const w = this.winch.at;
      this.rope.visible = true;
      this.rope.position.set(w.x, ropeTo, w.z);
      this.rope.scale.set(1, Math.max(0.01, w.y + 1.78 - ropeTo), 1);
    }
    for (const [id, m] of this.items) {
      if (seen.has(id)) continue;
      // Gone: a burst barrel, the key turned in its lock, the bell raised.
      this.group.remove(m);
      this.items.delete(id);
    }
  }

  private itemMesh(it: ExpItem): THREE.Mesh {
    const m = new THREE.Mesh(it.kind === 'barrel' ? this.kegG : it.kind === 'bell' ? this.bellG : this.keyG, it.kind === 'barrel' ? this.prop : this.iron);
    m.name = `item:${it.kind}`;
    m.castShadow = true;
    return m;
  }

  private syncWater(s: BattleState, x: Expedition, fresh: boolean): void {
    const wheels = x.devices.filter((d) => d.kind === 'wheel');
    this.drainedA = wheels.length > 0 && wheels.every((d) => d.done);
    if (this.waterA) this.waterA.visible = this.drainA > 0.01;
    if (!this.waterC || !this.ink) return;
    this.waterC.visible = !x.gateOpen;
    if (!x.gateOpen) {
      this.ink.visible = false;
      return;
    }
    // The ink rises from the moment the floodgate opens: the time left to raise the bell.
    if (!this.raised) this.inkK = x.lost === 'ink' ? 1 : clamp01(1 - (x.inkAt - s.tick) / Math.max(1, CELLARS.inkTicks));
    this.ink.visible = true;
    this.ink.position.y = 0.03 + this.inkK * 0.5;
    const c = this.ink.position;
    if (fresh && s.tick % 5 === 0) {
      const rm = this.layout.rooms[x.rooms.cistern];
      if (rm) {
        const h = (s.tick * 2654435761) >>> 0;
        const a = (h % 628) / 100;
        const r = ((h >>> 10) % 100) / 100 * rm.radius * S;
        const ctr = this.at(rm);
        this.fx.vfx.burst(new THREE.Vector3(ctr.x + Math.cos(a) * r, c.y + 0.02, ctr.z + Math.sin(a) * r), { n: 1, color: '#2a1640', speed: 0.004, up: 0.02, size: 0.22, life: 14, smoke: true });
      }
    }
  }

  /** The cellars' bursts (`ev boom`): a powder barrel, the rubble blown. Returns how hard the camera shakes, or null. */
  boom(kind: string, x: number, y: number, r: number): number | null {
    if (kind !== 'barrel' && kind !== 'rubble') return null;
    const px = x * K, pz = y * K;
    const p = new THREE.Vector3(px, this.ground(px, pz) + 0.05, pz);
    const R = (r / 1000) * S;
    const { vfx, low } = this.fx;
    if (kind === 'barrel') {
      vfx.ring(p, R * 0.2, R, '#ff7a2a', 8, 1);
      vfx.ring(p.clone().setY(p.y + 0.3), R * 0.1, R * 0.6, '#ffd0a0', 6, 0.7);
      vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 14 : 32, color: '#ff8a2a', speed: 0.13, up: 0.1, size: 0.24, life: 9 });
      vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 14, color: '#1e1a1c', speed: 0.06, up: 0.05, size: 0.7, life: 16, smoke: true, spread: 0.4 });
      vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: '#3a2a20', speed: 0.1, up: 0.1, size: 0.07, life: 14, gravity: 0.012 });
      this.fx.flare(p.clone().setY(p.y + 0.6), '#ff7a2a', 3.4, 7);
      return 0.45;
    }
    // The rubble: stone flung out of the doorway and a thick cloud of dust.
    vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 10 : 24, color: '#4a4c53', speed: 0.12, up: 0.11, size: 0.16, life: 14, gravity: 0.014 });
    vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 8 : 18, color: '#6d6458', speed: 0.05, up: 0.04, size: 0.8, life: 22, smoke: true, spread: 0.6 });
    vfx.ring(p, 0.2, 1.8, '#a9a49c', 8, 0.6);
    return 0.3;
  }

  /** The cellars' small moments (`ev skill`): a door raised, a lock or wheel or lever done, the bell hooked, picked up, lit. */
  skill(name: string, x: number, y: number): boolean {
    const px = x * K, pz = y * K;
    const p = new THREE.Vector3(px, this.ground(px, pz) + 0.05, pz);
    const { vfx, low } = this.fx;
    const near = (): DeviceView | undefined => {
      let best: DeviceView | undefined;
      let bd = 4;
      for (const v of this.devs) {
        const d = v.at.distanceToSquared(p);
        if (d < bd) {
          bd = d;
          best = v;
        }
      }
      return best;
    };
    switch (name) {
      case 'expDoor':
        vfx.burst(p.clone().setY(p.y + 1.3), { n: low ? 4 : 10, color: '#6d6458', speed: 0.02, up: -0.01, size: 0.4, life: 16, smoke: true, spread: 0.45 });
        vfx.ring(p, 0.2, 1.1, '#cfd3da', 6, 0.5);
        return true;
      case 'expLock':
      case 'expWheel':
      case 'expLever': {
        const v = near();
        if (v) v.flash = 1;
        const q = v ? v.at.clone().setY(v.at.y + 0.5) : p.clone().setY(p.y + 0.5);
        vfx.ring(q.clone().setY(q.y - 0.45), 0.2, 1.3, '#dfe4ff', 8, 0.9);
        vfx.burst(q, { n: low ? 5 : 12, color: name === 'expLock' ? '#cfd8e6' : '#ffffff', speed: 0.04, up: 0.05, size: 0.07, life: 9, gravity: 0.006 });
        this.fx.flare(q, '#9fb8d0', 1.5, 6);
        return true;
      }
      case 'expHooked': {
        vfx.ring(p, 0.2, 1.2, '#cfd8e6', 7, 0.8);
        vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 8, color: '#9aa4a8', speed: 0.03, up: 0.03, size: 0.08, life: 8 });
        return true;
      }
      case 'expRaised': {
        // The bell is up: a column of cold light down the well.
        vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 5), '#dfe4ff', 14, 0.6);
        vfx.ring(p, 0.4, 4.5, '#eef0ff', 14, 1);
        vfx.burst(p.clone().setY(p.y + 1), { n: low ? 14 : 34, color: '#ffffff', speed: 0.06, up: 0.12, size: 0.1, life: 16, gravity: 0.002, spread: 0.4 });
        this.fx.flare(p.clone().setY(p.y + 1.4), '#ffffff', 4, 10);
        return true;
      }
      case 'expPick':
        vfx.ring(p, 0.1, 0.6, '#cfd3da', 5, 0.6);
        return true;
      case 'expFuse':
        vfx.burst(p.clone().setY(p.y + 0.45), { n: low ? 5 : 12, color: '#ff8a2a', speed: 0.05, up: 0.06, size: 0.06, life: 8, gravity: 0.008 });
        this.fx.flare(p.clone().setY(p.y + 0.45), '#ff6a2a', 1.2, 5);
        return true;
    }
    return false;
  }

  /** Every animation frame: wheels and crank turning, the lever and bolts easing, the water draining, the ink rising. */
  frame(now: number): void {
    const dt = Math.min(0.1, Math.max(0, now - (this.lastNow || now)));
    this.lastNow = now;
    this.u.uTime.value = now % 3600;
    for (const v of this.devs) {
      const p = v.part;
      if (!p) continue;
      const base = p.userData.base as THREE.Vector3;
      if (v.kind === 'wheel' && v.turning) p.rotation.y += dt * 2.6;
      else if (v.kind === 'winch' && v.turning) p.rotation.x -= dt * 3.2;
      else if (v.kind === 'lever' || v.kind === 'lock') {
        v.ease += (v.want - v.ease) * Math.min(1, dt * (v.kind === 'lever' ? 8 : 5));
        // The lever: up and waiting, tilting as it is held, down once thrown. The bolt: drawn out of its staples.
        if (v.kind === 'lever') p.rotation.x = 0.15 + v.ease * 2.45;
        else p.position.x = base.x + v.ease * 0.32;
      }
    }
    if (this.waterA) {
      const want = this.drainedA ? 0 : 1;
      this.drainA += (want - this.drainA) * Math.min(1, dt * 0.6);
      if (this.drainedA && this.drainA < 0.02) this.drainA = 0;
      this.waterA.position.y = 0.025 + 0.045 * this.drainA + Math.sin(now * 0.8) * 0.003;
      (this.waterA.material as THREE.MeshStandardMaterial).opacity = 0.86 * Math.min(1, this.drainA * 1.6);
    }
    if (this.waterC) this.waterC.position.y = 0.07 + Math.sin(now * 0.7 + 1) * 0.003;
    if (this.ink?.visible) {
      this.ink.position.y = 0.03 + this.inkK * 0.5 + Math.sin(now * 0.5) * 0.004;
      (this.ink.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.4 + 0.15 * Math.sin(now * 1.3);
    }
    if (this.raised && this.bellGone < 0.4) this.bellGone += dt * 0.3;
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.geometry.dispose();
    });
    this.kegG.dispose();
    this.bellG.dispose();
    this.keyG.dispose();
    for (const m of this.mats) m.dispose();
  }
}
